import {
  addMinutes,
  Button,
  ButtonColor,
  DateFormat,
  formatDate,
  formatPrice,
  pluralize,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React from 'react';

import { formatTimeRange } from '../../utils/datetime';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { GroupClassImage } from '../classes/GroupClassImage';

import { type CoachClassCardClassSessionFragment } from './__generated-gql-types__/CoachClassCard.generated';

interface CoachClassCardProps {
  name: string;
  imageLink: string;
  date: number;
  durationInMinute: number;
  link: string;
  price?: number | null;
  otherSessions?: CoachClassCardClassSessionFragment[] | null;
}

const CoachClassCard: React.FC<CoachClassCardProps> = ({
  name,
  imageLink,
  date,
  durationInMinute,
  link,
  price,
  otherSessions,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);

  const CARD = (
    <div className="flex items-center justify-between rounded-lg border border-leland-gray-stroke p-4 shadow-sm lg:space-x-4">
      <div className="flex items-start justify-start space-x-4 lg:items-center">
        <div className="flex shrink-0 items-center">
          <GroupClassImage
            containerClassName="shrink-0 w-32"
            className="rounded-md"
            name={name}
            imageLink={imageLink}
            sizes={{ dflt: '128px' }}
          />
        </div>
        <div className="flex flex-col items-start justify-center space-y-0.5">
          <h3 className="line-clamp-1 break-all text-lg font-medium leading-6 text-leland-gray-dark">
            {name}
          </h3>
          <div className="flex items-center justify-start text-base font-normal text-leland-gray-light">
            <p>
              {formatDate(date, DateFormat.SHORT_DAY_OF_WEEK_MONTH_DAY)} from{' '}
              {formatTimeRange(date, addMinutes(date, durationInMinute))}
            </p>
            {isMd && otherSessions && otherSessions.length > 0 ? (
              <>
                <div className="mx-2 my-1 inline-flex size-0.5 rounded-full bg-leland-gray-light" />
                <Tooltip
                  content={
                    <p className="whitespace-pre">
                      {otherSessions
                        .map(
                          (session) =>
                            `${formatDate(
                              session.startAt,
                              DateFormat.SHORT_DAY_OF_WEEK_MONTH_DAY,
                            )} from ${formatTimeRange(
                              session.startAt,
                              addMinutes(
                                session.startAt,
                                session.durationInMinute,
                              ),
                            )}`,
                        )
                        .join('\r\n')}
                    </p>
                  }
                >
                  <p className="underline">
                    {pluralize(otherSessions.length, 'more time')} available
                  </p>
                </Tooltip>
              </>
            ) : null}
          </div>
          {price && price > 0 ? (
            <p className="text-base font-medium text-leland-primary">
              {formatPrice(price)} per attendee
            </p>
          ) : null}
        </div>
      </div>
      <div className="hidden h-full flex-col items-end justify-end lg:flex">
        <Link
          href={link}
          className="mt-5"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button label="Learn more" buttonColor={ButtonColor.WHITE} />
        </Link>
      </div>
    </div>
  );

  return isMd ? (
    CARD
  ) : (
    <Link href={link} target="_blank" rel="noopener noreferrer">
      {CARD}
    </Link>
  );
};

export default CoachClassCard;
